/**
 * Template Credit Calculator
 * 
 * Berechnet faire Credit-Kosten basierend auf Feld-Komplexität
 * Verwendet ein 3-Tier Rabatt-System:
 * - Micro Fields (options, tags, sku): 50% Rabatt
 * - Small Fields (vendor, product_type, handle): 25% Rabatt
 * - Normal/Large Fields (title, body_html, metafields): Kein Rabatt
 */

/**
 * Feld-Kategorien für Credit-Berechnung
 */
const FIELD_CATEGORIES = {
  // MICRO FIELDS - Einzelne Wörter, minimale AI-Arbeit (50% Rabatt)
  micro: [
    'option1_name',
    'option2_name',
    'option3_name',
    'option1_values',
    'option2_values',
    'option3_values',
    'tags',
    'variants.sku'
  ],
  
  // SMALL FIELDS - Kurze Texte, wenig AI-Arbeit (25% Rabatt)
  small: [
    'vendor',
    'product_type',
    'handle'
  ]
  
  // NORMAL/LARGE FIELDS - Alles andere (kein Rabatt)
  // Includes: title, body_html, description, metafields, variants.price, etc.
};

/**
 * Berechnet einen fairen Credit-Multiplikator basierend auf Feld-Komplexität
 * Nur Rabatte, keine Aufschläge
 * 
 * @param {Object} template - Das Template-Objekt
 * @returns {number} Multiplikator zwischen 0.5 und 1.0
 */
export const getTemplateCostMultiplier = (template) => {
  const inputFields = template.inputFields || [];
  const outputFields = template.outputFields || [];
  const allFields = [...inputFields, ...outputFields];
  
  // Wenn keine Felder, Standard-Multiplikator
  if (allFields.length === 0) return 1;
  
  // Zähle Felder pro Kategorie
  let microCount = 0;
  let smallCount = 0;
  let normalCount = 0;
  
  allFields.forEach(field => {
    if (FIELD_CATEGORIES.micro.includes(field)) {
      microCount++;
    } else if (FIELD_CATEGORIES.small.includes(field)) {
      smallCount++;
    } else {
      normalCount++;
    }
  });
  
  const totalFields = allFields.length;
  const microPercent = microCount / totalFields;
  const smallPercent = smallCount / totalFields;
  
  // Rabatt-Logik basierend auf Feld-Verteilung
  
  // Wenn >80% micro fields → 50% Rabatt
  if (microPercent > 0.8) {
    return 0.5;
  }
  
  // Wenn >50% micro fields → 33% Rabatt
  if (microPercent > 0.5) {
    return 0.67;
  }
  
  // Wenn >50% small fields → 25% Rabatt
  if (smallPercent > 0.5) {
    return 0.75;
  }
  
  // Wenn Mix aus micro+small >50% → 20% Rabatt
  if ((microPercent + smallPercent) > 0.5) {
    return 0.8;
  }
  
  // Alles andere: Kein Rabatt (body_html, title, metafields, etc.)
  return 1;
};

/**
 * Berechnet die finalen Credits für ein Template
 * 
 * @param {Object} template - Das Template-Objekt
 * @param {number} aiModelCost - Die Basis-Kosten des AI-Modells (z.B. 1 für GPT-4o-mini, 3 für GPT-4)
 * @returns {number} Finale Credit-Kosten (immer ganze Zahl)
 */
export const calculateTemplateCredits = (template, aiModelCost = 1) => {
  // Hole den Multiplikator basierend auf Feld-Komplexität
  const multiplier = getTemplateCostMultiplier(template);
  
  // Berechne finale Credits
  const rawCredits = aiModelCost * multiplier;
  
  // Runde auf ganze Zahl (immer aufrunden, mindestens 1)
  const finalCredits = Math.max(1, Math.ceil(rawCredits));
  
  return finalCredits;
};

/**
 * Gibt eine detaillierte Credit-Berechnung zurück (für Debugging/Anzeige)
 * 
 * @param {Object} template - Das Template-Objekt
 * @param {number} aiModelCost - Die Basis-Kosten des AI-Modells
 * @returns {Object} Detaillierte Berechnung
 */
export const getDetailedCreditCalculation = (template, aiModelCost = 1) => {
  const inputFields = template.inputFields || [];
  const outputFields = template.outputFields || [];
  const allFields = [...inputFields, ...outputFields];
  
  let microCount = 0;
  let smallCount = 0;
  let normalCount = 0;
  
  allFields.forEach(field => {
    if (FIELD_CATEGORIES.micro.includes(field)) {
      microCount++;
    } else if (FIELD_CATEGORIES.small.includes(field)) {
      smallCount++;
    } else {
      normalCount++;
    }
  });
  
  const multiplier = getTemplateCostMultiplier(template);
  const finalCredits = calculateTemplateCredits(template, aiModelCost);
  
  return {
    totalFields: allFields.length,
    microFields: microCount,
    smallFields: smallCount,
    normalFields: normalCount,
    multiplier: multiplier,
    aiModelCost: aiModelCost,
    rawCredits: aiModelCost * multiplier,
    finalCredits: finalCredits,
    discount: multiplier < 1 ? Math.round((1 - multiplier) * 100) : 0
  };
};
